import React, { useState, useEffect } from "react"
import { lumi } from "../lib/lumi"
import {Search, Eye, Download, Filter} from 'lucide-react'

interface Order {
  _id: string
  orderNumber: string
  customerId: string
  customerName?: string
  customerEmail?: string
  items: Array<{
    productId: string
    productName: string
    quantity: number
    price: number
    subtotal: number
  }>
  subtotal: number
  shippingFee: number
  discount: number
  totalAmount: number
  status: "pending" | "paid" | "processing" | "shipped" | "completed" | "cancelled" | "refunded"
  paymentMethod: "cash" | "creditCard" | "linePay" | "transfer" | "net30"
  paymentStatus: "unpaid" | "paid" | "refunded"
  shippingAddress?: {
    recipient: string
    phone: string
    fullAddress: string
    city: string
    district: string
    postalCode: string
  }
  couponCode?: string
  pointsUsed?: number
  pointsEarned?: number
  orderType: "online" | "inStore" | "b2b"
  storeId?: string
  notes?: string
  spentRecorded?: boolean // 是否已记录到会员累积消费
  createdAt: string
  updatedAt: string
}

const statusLabels: Record<string, string> = {
  pending: "待付款",
  paid: "已付款",
  processing: "處理中",
  shipped: "已出貨",
  completed: "已完成",
  cancelled: "已取消",
  refunded: "已退款"
}

const statusColors: Record<string, string> = {
  pending: "bg-yellow-600",
  paid: "bg-blue-600",
  processing: "bg-purple-600",
  shipped: "bg-orange-600",
  completed: "bg-green-600",
  cancelled: "bg-red-600",
  refunded: "bg-gray-600"
}

const paymentMethodLabels: Record<string, string> = {
  cash: "超商付款",
  creditCard: "信用卡",
  linePay: "LINE Pay",
  transfer: "ATM轉帳",
  net30: "月結 30 天",
  newebpay: "信用卡"
}

const shippingMethodLabels: Record<string, string> = {
  home: "宅配到府",
  store: "門市自取",
  "711": "7-11 超商",
  family: "全家超商"
}

interface OrderManagerProps {
  initialSearchEmail?: string
}

const OrderManager: React.FC<OrderManagerProps> = ({ initialSearchEmail = "" }) => {
  const [orders, setOrders] = useState<Order[]>([])
  const [filteredOrders, setFilteredOrders] = useState<Order[]>([])
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState(initialSearchEmail)
  const [filterStatus, setFilterStatus] = useState<string>("all")
  const [filterType, setFilterType] = useState<string>("all")
  const [filterShipping, setFilterShipping] = useState<string>("all")
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null)
  const [showDetail, setShowDetail] = useState(false)

  useEffect(() => {
    fetchOrders()
  }, [])

  // 当传入初始搜索email时，自动设置搜索条件
  useEffect(() => {
    if (initialSearchEmail) {
      setSearchTerm(initialSearchEmail)
    }
  }, [initialSearchEmail])

  useEffect(() => {
    applyFilters()
  }, [orders, searchTerm, filterStatus, filterType, filterShipping])

  const fetchOrders = async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.orders.list({
        sort: { createdAt: -1 }
      })
      setOrders(list)
    } catch (error) {
      console.error("Failed to fetch orders:", error)
    } finally {
      setLoading(false)
    }
  }

  const applyFilters = () => {
    let filtered = orders

    if (searchTerm) {
      filtered = filtered.filter(
        (o) =>
          o.orderNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          o.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          o.customerEmail?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    if (filterStatus !== "all") {
      filtered = filtered.filter((o) => o.status === filterStatus)
    }

    if (filterType !== "all") {
      filtered = filtered.filter((o) => o.orderType === filterType)
    }

    if (filterShipping !== "all") {
      filtered = filtered.filter((o) => o.shippingMethod === filterShipping)
    }

    setFilteredOrders(filtered)
  }

  const updateOrderStatus = async (orderId: string, newStatus: Order["status"]) => {
    try {
      // 获取完整订单信息（包含 spentRecorded 标记）
      const order = await lumi.entities.orders.get(orderId)
      if (!order) return

      const oldStatus = order.status
      const wasSpentRecorded = order.spentRecorded || false

      // 判断是否需要更新会员累积消费
      // 应该累计消费的状态：paid, completed
      const isPaidStatus = (status: string) => status === "paid" || status === "completed"
      
      // 当状态变为 paid/completed 且之前未记录时，累加消费和积分
      const shouldAddSpent = 
        isPaidStatus(newStatus) && 
        !wasSpentRecorded &&
        order.customerEmail

      // 当状态从 paid/completed 变为其他状态（pending/processing/cancelled/refunded）且之前已记录时，扣除消费和积分
      const shouldRemoveSpent = 
        !isPaidStatus(newStatus) &&
        wasSpentRecorded &&
        order.customerEmail

      // 更新订单状态
      const updateData: any = {
        status: newStatus,
        updatedAt: new Date().toISOString()
      }

      // 标记是否已累计
      if (shouldAddSpent) {
        updateData.spentRecorded = true
      } else if (shouldRemoveSpent) {
        updateData.spentRecorded = false
      }

      await lumi.entities.orders.update(orderId, updateData)

      // 增加累积消费（首次变为 paid/completed）
      if (shouldAddSpent) {
        try {
          const { list: customerList } = await lumi.entities.customers.list({
            filter: { email: order.customerEmail }
          })

          // 计算获得的积分：消费 1 元 = 1 积分
          const earnedPoints = Math.floor(order.totalAmount || 0)

          if (customerList.length > 0) {
            const customer = customerList[0]
            const balanceBefore = customer.points || 0
            const balanceAfter = balanceBefore + earnedPoints

            await lumi.entities.customers.update(customer._id, {
              totalSpent: (customer.totalSpent || 0) + (order.totalAmount || 0),
              orderCount: (customer.orderCount || 0) + 1,
              points: balanceAfter,
              lastPurchaseAt: new Date().toISOString()
            })

            // 创建点数交易记录（正值表示获得）
            await lumi.entities.points_transactions.create({
              customerId: customer._id,
              customerEmail: order.customerEmail,
              customerName: order.customerName,
              type: 'earned',
              points: earnedPoints,
              balanceBefore: balanceBefore,
              balanceAfter: balanceAfter,
              orderId: order._id,
              orderNumber: order.orderNumber,
              amount: order.totalAmount,
              description: `订单完成获得积分 (${order.orderNumber}: ${statusLabels[newStatus]})`,
              createdAt: new Date().toISOString(),
              createdBy: 'admin'
            })

            console.log(`✅ 累加消费: ${order.customerEmail} +$${order.totalAmount}，獲得 ${earnedPoints} 積分（已记录交易）`)
          }
        } catch (error) {
          console.error("累加消费失败:", error)
        }
      }

      // 减少累积消费（从 paid/completed 变为 cancelled/refunded）
      if (shouldRemoveSpent) {
        try {
          const { list: customerList } = await lumi.entities.customers.list({
            filter: { email: order.customerEmail }
          })

          // 计算需要扣除的积分
          const deductPoints = Math.floor(order.totalAmount || 0)

          if (customerList.length > 0) {
            const customer = customerList[0]
            const balanceBefore = customer.points || 0
            const balanceAfter = Math.max(0, balanceBefore - deductPoints)

            await lumi.entities.customers.update(customer._id, {
              totalSpent: Math.max(0, (customer.totalSpent || 0) - (order.totalAmount || 0)),
              orderCount: Math.max(0, (customer.orderCount || 0) - 1),
              points: balanceAfter
            })

            // 创建点数交易记录（负值表示扣除）
            await lumi.entities.points_transactions.create({
              customerId: customer._id,
              customerEmail: order.customerEmail,
              customerName: order.customerName,
              type: 'refund',
              points: -deductPoints,
              balanceBefore: balanceBefore,
              balanceAfter: balanceAfter,
              orderId: order._id,
              orderNumber: order.orderNumber,
              amount: order.totalAmount,
              description: `订单状态变更扣除积分 (${order.orderNumber}: ${statusLabels[oldStatus]} → ${statusLabels[newStatus]})`,
              createdAt: new Date().toISOString(),
              createdBy: 'admin'
            })

            console.log(`✅ 扣除消费: ${order.customerEmail} -$${order.totalAmount}，扣除 ${deductPoints} 積分（已记录交易）`)
          }
        } catch (error) {
          console.error("扣除消费失败:", error)
        }
      }

      // 发送订单状态更新邮件通知
      if (order.customerEmail && oldStatus !== newStatus) {
        try {
          await lumi.functions.invoke('sendOrderStatusUpdate', {
            method: 'POST',
            body: {
              orderId: order.orderNumber,
              customerEmail: order.customerEmail,
              customerName: order.customerName || '客戶',
              oldStatus: oldStatus,
              newStatus: newStatus,
              updateDate: new Date().toLocaleString('zh-TW'),
              orderTotal: order.totalAmount || 0
            }
          })
          console.log(`✅ 订单状态更新邮件已发送: ${order.orderNumber} (${oldStatus} → ${newStatus})`)
        } catch (error) {
          console.error("发送订单状态更新邮件失败:", error)
          // 邮件发送失败不影响订单状态更新，仅记录错误
        }
      }

      await fetchOrders()
      
      // 关闭 Modal
      setShowDetail(false)
      setSelectedOrder(null)
    } catch (error) {
      console.error("Failed to update order status:", error)
    }
  }

  const exportToCSV = () => {
    const headers = ["訂單編號", "客戶", "訂單類型", "總金額", "狀態", "付款方式", "建立日期"]
    const rows = filteredOrders.map((o) => [
      o.orderNumber,
      o.customerName || "",
      o.orderType === "online" ? "線上" : o.orderType === "inStore" ? "店內" : "B2B",
      o.totalAmount,
      statusLabels[o.status],
      paymentMethodLabels[o.paymentMethod],
      new Date(o.createdAt).toLocaleDateString("zh-TW")
    ])

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.join(","))
    ].join("\n")

    const blob = new Blob(["\ufeff" + csvContent], { type: "text/csv;charset=utf-8;" })
    const link = document.createElement("a")
    link.href = URL.createObjectURL(blob)
    link.download = `訂單資料_${new Date().toISOString().split("T")[0]}.csv`
    link.click()
  }

  const viewDetail = (order: Order) => {
    setSelectedOrder(order)
    setShowDetail(true)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">載入中...</div>
      </div>
    )
  }

  const totalRevenue = orders
    .filter((o) => o.status !== "cancelled" && o.status !== "refunded")
    .reduce((sum, o) => sum + (o.totalAmount || 0), 0)

  return (
    <div className="p-8 h-full flex flex-col">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-3xl font-light tracking-wider mb-2">訂單管理</h2>
          <p className="text-gray-400">管理所有訂單與出貨狀態</p>
        </div>
        <button
          onClick={exportToCSV}
          className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white hover:bg-green-700 transition-colors"
        >
          <Download size={20} />
          匯出 CSV
        </button>
      </div>

      <div className="mb-6 grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="md:col-span-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={20} />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="搜尋訂單編號、客戶姓名、Email..."
              className="w-full pl-10 pr-4 py-3 bg-gray-950 border border-gray-800 text-gray-100 focus:outline-none focus:border-gray-600"
            />
          </div>
        </div>

        <div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="w-full px-4 py-3 bg-gray-950 border border-gray-800 text-gray-100 focus:outline-none focus:border-gray-600"
          >
            <option value="all">所有狀態</option>
            {Object.entries(statusLabels).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>

        <div>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="w-full px-4 py-3 bg-gray-950 border border-gray-800 text-gray-100 focus:outline-none focus:border-gray-600"
          >
            <option value="all">所有類型</option>
            <option value="online">線上訂單</option>
            <option value="inStore">店內訂單</option>
            <option value="b2b">B2B 訂單</option>
          </select>
        </div>

        <div>
          <select
            value={filterShipping}
            onChange={(e) => setFilterShipping(e.target.value)}
            className="w-full px-4 py-3 bg-gray-950 border border-gray-800 text-gray-100 focus:outline-none focus:border-gray-600"
          >
            <option value="all">所有配送方式</option>
            <option value="宅配到府">宅配到府</option>
            <option value="門市自取">門市自取</option>
            <option value="超商取貨">超商取貨</option>
          </select>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-gray-950 border border-gray-800 p-6">
          <div className="text-3xl font-light text-gray-100 mb-2">{orders.length}</div>
          <div className="text-sm text-gray-400">總訂單數</div>
        </div>
        <div className="bg-gray-950 border border-gray-800 p-6">
          <div className="text-3xl font-light text-gray-100 mb-2">
            {orders.filter((o) => o.status === "pending" || o.status === "paid").length}
          </div>
          <div className="text-sm text-gray-400">待處理</div>
        </div>
        <div className="bg-gray-950 border border-gray-800 p-6">
          <div className="text-3xl font-light text-gray-100 mb-2">
            ${totalRevenue.toLocaleString()}
          </div>
          <div className="text-sm text-gray-400">總營收</div>
        </div>
        <div className="bg-gray-950 border border-gray-800 p-6">
          <div className="text-3xl font-light text-gray-100 mb-2">
            {orders.filter((o) => o.status === "completed").length}
          </div>
          <div className="text-sm text-gray-400">已完成</div>
        </div>
      </div>

      <div className="bg-gray-950 border border-gray-800 overflow-x-auto">
        <table className="w-full">
          <thead className="border-b border-gray-800">
            <tr className="text-left">
              <th className="px-6 py-4 text-sm font-light text-gray-400">訂單編號</th>
              <th className="px-6 py-4 text-sm font-light text-gray-400">客戶</th>
              <th className="px-6 py-4 text-sm font-light text-gray-400">類型</th>
              <th className="px-6 py-4 text-sm font-light text-gray-400">配送方式</th>
              <th className="px-6 py-4 text-sm font-light text-gray-400">總金額</th>
              <th className="px-6 py-4 text-sm font-light text-gray-400">狀態</th>
              <th className="px-6 py-4 text-sm font-light text-gray-400">付款</th>
              <th className="px-6 py-4 text-sm font-light text-gray-400">建立日期</th>
              <th className="px-6 py-4 text-sm font-light text-gray-400">操作</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrders.map((order) => (
              <tr key={order._id} className="border-b border-gray-800 hover:bg-gray-900 transition-colors">
                <td className="px-6 py-4">
                  <code className="text-gray-100 font-mono text-sm">{order.orderNumber}</code>
                </td>
                <td className="px-6 py-4">
                  <div>
                    <div className="text-gray-100">{order.customerName || "訪客"}</div>
                    {order.customerEmail && (
                      <div className="text-xs text-gray-500">{order.customerEmail}</div>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className="px-2 py-1 bg-gray-800 text-gray-300 text-xs">
                    {order.orderType === "online" ? "線上" : order.orderType === "inStore" ? "店內" : "B2B"}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <span className="px-2 py-1 bg-blue-900 text-blue-300 text-xs">
                    {order.shippingMethod || "宅配到府"}
                  </span>
                </td>
                <td className="px-6 py-4 text-gray-100">${(order.totalAmount || 0).toLocaleString()}</td>
                <td className="px-6 py-4">
                  <span className={`px-3 py-1 ${statusColors[order.status]} text-white text-xs`}>
                    {statusLabels[order.status]}
                  </span>
                </td>
                <td className="px-6 py-4 text-gray-300 text-sm">
                  {paymentMethodLabels[order.paymentMethod]}
                </td>
                <td className="px-6 py-4 text-gray-400 text-sm">
                  {new Date(order.createdAt).toLocaleDateString("zh-TW")}
                </td>
                <td className="px-6 py-4">
                  <button
                    onClick={() => viewDetail(order)}
                    className="p-2 hover:bg-gray-800 transition-colors"
                    title="查看詳情"
                  >
                    <Eye size={16} className="text-gray-400" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredOrders.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            {searchTerm || filterStatus !== "all" || filterType !== "all" || filterShipping !== "all"
              ? "無符合條件的訂單"
              : "尚無訂單資料"}
          </div>
        )}
      </div>
      </div>

      {showDetail && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-950 border border-gray-800 max-w-3xl w-full max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-gray-800 flex items-center justify-between flex-shrink-0">
              <div>
                <h3 className="text-xl font-light tracking-wider">訂單詳情</h3>
                <code className="text-sm text-gray-500">{selectedOrder.orderNumber}</code>
              </div>
              <button onClick={() => setShowDetail(false)} className="text-gray-400 hover:text-gray-200">
                ✕
              </button>
            </div>

            <div className="p-6 space-y-6 overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-gray-400 mb-1">客戶姓名</div>
                  <div className="text-gray-100">{selectedOrder.customerName || "訪客"}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-400 mb-1">Email</div>
                  <div className="text-gray-100">{selectedOrder.customerEmail || "未提供"}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-400 mb-1">訂單狀態</div>
                  <select
                    value={selectedOrder.status}
                    onChange={(e) => updateOrderStatus(selectedOrder._id, e.target.value as Order["status"])}
                    className="px-3 py-1 bg-gray-800 border border-gray-700 text-gray-100 text-sm focus:outline-none focus:border-gray-600"
                  >
                    {Object.entries(statusLabels).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <div className="text-sm text-gray-400 mb-1">付款方式</div>
                  <div className="text-gray-100">
                    {selectedOrder.paymentMethod 
                      ? (paymentMethodLabels[selectedOrder.paymentMethod] || selectedOrder.paymentMethod)
                      : "未指定"}
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-800 pt-4">
                <div className="text-sm text-gray-400 mb-2">配送方式</div>
                <div className="text-gray-100 mb-3 font-medium">
                  {selectedOrder.shippingMethod || "宅配到府"}
                </div>

                {selectedOrder.shippingMethod === "門市自取" && (
                  <div className="bg-gray-900 border border-gray-800 p-4">
                    {selectedOrder.shippingAddress?.storeName && (
                      <>
                        <div className="text-sm text-gray-400 mb-1">取貨門市</div>
                        <div className="text-gray-100 mb-3">{selectedOrder.shippingAddress.storeName}</div>
                      </>
                    )}
                    {selectedOrder.shippingAddress?.storeAddress && (
                      <>
                        <div className="text-sm text-gray-400 mb-1">門市地址</div>
                        <div className="text-gray-300 text-sm">{selectedOrder.shippingAddress.storeAddress}</div>
                      </>
                    )}
                  </div>
                )}

                {selectedOrder.shippingMethod === "超商取貨" && (
                  <div className="bg-gray-900 border border-gray-800 p-4">
                    {selectedOrder.shippingAddress?.storeName && (
                      <>
                        <div className="text-sm text-gray-400 mb-1">取貨門市</div>
                        <div className="text-gray-100 mb-3">{selectedOrder.shippingAddress.storeName}</div>
                      </>
                    )}
                    {selectedOrder.shippingAddress?.storeAddress && (
                      <>
                        <div className="text-sm text-gray-400 mb-1">門市地址</div>
                        <div className="text-gray-300 text-sm">{selectedOrder.shippingAddress.storeAddress}</div>
                      </>
                    )}
                  </div>
                )}

                {(selectedOrder.shippingMethod === "宅配到府" || !selectedOrder.shippingMethod) && selectedOrder.shippingAddress && (
                  <div className="bg-gray-900 border border-gray-800 p-4">
                    <div className="text-sm text-gray-400 mb-1">收件資訊</div>
                    <div className="text-gray-100 mb-3">
                      {selectedOrder.shippingAddress.recipient} / {selectedOrder.shippingAddress.phone}
                    </div>
                    <div className="text-sm text-gray-400 mb-1">配送地址</div>
                    <div className="text-gray-300 text-sm">{selectedOrder.shippingAddress.fullAddress}</div>
                  </div>
                )}
              </div>

              <div className="border-t border-gray-800 pt-4">
                <div className="text-sm text-gray-400 mb-3">訂單商品</div>
                <div className="space-y-2">
                  {selectedOrder.items.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center py-2 border-b border-gray-800">
                      <div>
                        <div className="text-gray-100">{item.productName}</div>
                        <div className="text-sm text-gray-500">
                          ${item.price} × {item.quantity}
                        </div>
                      </div>
                      <div className="text-gray-100">${(item.subtotal || 0).toLocaleString()}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t border-gray-800 pt-4 space-y-2">
                <div className="flex justify-between text-gray-300">
                  <span>小計</span>
                  <span>${(selectedOrder.subtotal || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-gray-300">
                  <span>運費</span>
                  <span>${(selectedOrder.shippingFee || 0).toLocaleString()}</span>
                </div>
                {(selectedOrder.discount || 0) > 0 && (
                  <div className="flex justify-between text-green-400">
                    <span>折扣 {selectedOrder.couponCode && `(${selectedOrder.couponCode})`}</span>
                    <span>-${(selectedOrder.discount || 0).toLocaleString()}</span>
                  </div>
                )}
                {(selectedOrder.pointsUsed || 0) > 0 && (
                  <div className="flex justify-between text-blue-400">
                    <span>點數折抵 ({selectedOrder.pointsUsed} 點)</span>
                    <span>-${Math.floor((selectedOrder.pointsUsed || 0) / 100).toLocaleString()}</span>
                  </div>
                )}
                <div className="flex justify-between text-xl font-light text-gray-100 pt-2 border-t border-gray-800">
                  <span>總計</span>
                  <span>${(selectedOrder.totalAmount || 0).toLocaleString()}</span>
                </div>
              </div>

              {selectedOrder.notes && (
                <div className="border-t border-gray-800 pt-4">
                  <div className="text-sm text-gray-400 mb-1">訂單備註</div>
                  <div className="text-gray-300">{selectedOrder.notes}</div>
                </div>
              )}

              <div className="border-t border-gray-800 pt-4 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-gray-400 mb-1">建立時間</div>
                  <div className="text-gray-300">{new Date(selectedOrder.createdAt).toLocaleString("zh-TW")}</div>
                </div>
                <div>
                  <div className="text-gray-400 mb-1">更新時間</div>
                  <div className="text-gray-300">{new Date(selectedOrder.updatedAt).toLocaleString("zh-TW")}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default OrderManager
